"use client";

import Link, { LinkProps } from "next/link";
import { MouseEvent, PropsWithChildren } from "react";
import { track } from "@lib/track"; // adjust path if needed

type CommonProps = {
  /** Visual design */
  variant?: "solid" | "outline" | "ghost" | "text";
  size?: "sm" | "md" | "lg";
  color?: "base" | "duck" | "oat" | "neutral";
  className?: string;
  disabled?: boolean;
  /** Analytics */
  event?: string;
  label?: string;
  component?: string;
  meta?: Record<string, unknown>;
};

type ButtonProps = CommonProps & {
  href?: undefined;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
};

type LinkishProps = CommonProps &
  Omit<LinkProps, "href"> & {
    href: string;
    newTab?: boolean;
    prefetch?: boolean;
  };

type ActionProps = PropsWithChildren<ButtonProps | LinkishProps>;

const variantClasses = {
  solid: "text-[var(--color-text-brand)] hover:opacity-90",
  outline:
    "border border-[var(--color-text-primary)] text-[var(--color-text-primary)] hover:text-[var(--color-link-hover)] hover:border-[var(--color-link-hover)]",
  ghost:
    "text-[var(--color-brand)] hover:bg-[var(--color-brand)]/10",
  text: "text-[var(--color-brand)] underline underline-offset-4 hover:opacity-80",
};

const sizeClasses = {
  sm: "text-sm px-3 py-1.5 rounded-full",
  md: "text-sm md:text-base px-4 py-2 rounded-full",
  lg: "text-base md:text-lg px-5 py-3 rounded-full",
};

const colorOverrides = {
  duck: {
    solid: "bg-[var(--fern-dk)] text-[var(--text-on-dark)] hover:opacity-90",
    outline:
      "border border-[var(--color-brand)]/40 text-[var(--color-brand)]/40 hover:bg-[var(--color-brand)] hover:text-[var(--btn-anchor-text)]",
    ghost: "text-[var(--fern-dk)] hover:bg-[var(--mint)]",
    text: "text-[var(--fern-dk)] underline-offset-4 hover:opacity-80",
  },
  oat: {
    solid: "bg-[var(--cream)] text-[var(--ink)] hover:bg-[var(--cream-dk)]",
    outline:
      "border border-[var(--text-on-dark)] text-[var(--text-on-dark)] hover:bg-[var(--text-on-dark)]/10",
    ghost: "text-[var(--text-on-dark)] hover:bg-[var(--text-on-dark)]/10",
    text: "text-[var(--text-on-dark)] underline-offset-4 hover:opacity-80",
  },
  neutral: {
    solid: "bg-[var(--color-brand)] text-white hover:opacity-50",
    outline:
      "border border-[var(--color-border)] text-[var(--color-text-ink)] hover:bg-[var(--color-brand)]/10",
    ghost: "text-[var(--color-text-ink)] hover:bg-[var(--color-text-ink)]/10",
    text: "text-[var(--color-text-ink)] underline-offset-4 hover:opacity-80",
  },
  base: {
    solid: "bg-[var(--color-brand)] text-white hover:opacity-90",
    outline: "border border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-white",
    ghost: "text-[var(--color-brand)] hover:bg-[var(--color-brand)]/10",
    text: "text-[var(--color-brand)] underline underline-offset-4 hover:opacity-80",
  },
};

function cx(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(" ");
}

function fireTrack({
  event = "click",
  label,
  component,
  meta,
}: Pick<CommonProps, "event" | "label" | "component" | "meta">) {
  try {
    track(
      event, // Event name
      {
        label: label || "", // Metadata: label
        component: component || "", // Metadata: component
      },
      {
        event, // Event name for TrackEvent
        label, // Event label
        component, // Component name
        ...(meta ? { meta } : {}), // Include meta if it exists
      }
    );
  } catch {
    // silent fail
  }
}

export default function Action(props: ActionProps) {
  const {
    children,
    variant = "solid",
    size = "md",
    color = "base",
    className,
    disabled,
    event,
    label,
    component,
    meta,
  } = props;

  const base =
    colorOverrides[color]?.[variant as keyof (typeof colorOverrides)[typeof color]] ??
    variantClasses[variant];

  const cls = cx(
    "inline-flex items-center justify-center transition focus-visible:ring-2 focus-visible:ring-black/20 disabled:opacity-60 disabled:cursor-not-allowed",
    base,
    sizeClasses[size],
    className
  );

  if ("href" in props && props.href) {
    const {
      href, newTab, prefetch, onClick,
      // Strip TrackedLink-specific props so they don't land as HTML attributes or override cls
      variant: _v, size: _sz, color: _c,
      event: _e, label: _l, component: _co, meta: _m,
      className: _className, disabled: _dis,
      ...rest
    } = props as LinkishProps & {
      onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    };

    return (
      <Link
        href={href}
        prefetch={prefetch}
        target={newTab ? "_blank" : undefined}
        rel={newTab ? "noopener noreferrer" : undefined}
        aria-disabled={disabled}
        className={cls}
        onClick={(e) => {
          if (disabled) return;
          fireTrack({ event, label, component, meta });
          onClick?.(e);
        }}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const { onClick, type = "button" } = props as ButtonProps;
  return (
    <button
      type={type}
      disabled={disabled}
      className={cls}
      onClick={(e) => {
        if (disabled) return;
        fireTrack({ event, label, component, meta });
        onClick?.(e);
      }}
    >
      {children}
    </button>
  );
}

